import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Card,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  SearchField,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { supabase } from '@/shared/lib/supabase';
import { formatZARFromCents } from '@/shared/utils/format';

/**
 * /admin/services/installment-recovery
 *
 * Phase 10 Slice 10.3, T-10.3-06.
 *
 * Lists at-risk installment_schedule rows. Powered by the Track B escalation
 * mechanism — rows where last_failure_at is set and escalation hasn't fully
 * run out (escalation_step < 6).
 *
 * Staff use this page to:
 *   - See which customers are mid-recovery
 *   - Filter by escalation step (which day-band they're in)
 *   - Click into the linked service order for context + ticket
 *
 * Cron handles email + provisioning automation. Day 2 + Day 7 personal
 * contacts are staff-driven; this page makes them visible.
 */

interface RecoveryRow {
  id: string;
  service_order_id: string;
  customer_id: string;
  customer_email: string | null;
  plan_months: number;
  installments_paid: number;
  installments_total: number;
  installment_cents: number;
  last_failure_at: string;
  escalation_step: number;
  total_paid_cents: number;
}

const ESCALATION_LABEL: Record<number, { label: string; tone: StatusTone }> = {
  0: { label: 'Just failed (day 0–1)', tone: 'pending' },
  1: { label: 'Day 2 — staff to contact', tone: 'pending' },
  2: { label: 'Day 7 — settlement options', tone: 'pending' },
  3: { label: 'Day 14 — warning sent', tone: 'failed' },
  4: { label: 'Day 30 — hosting suspended', tone: 'failed' },
  5: { label: 'Day 45 — domain review', tone: 'failed' },
  6: { label: 'Day 60 — terminated', tone: 'failed' },
};

function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export default function InstallmentRecoveryPage() {
  const [rows, setRows] = useState<RecoveryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data, error: dbErr } = await supabase
          .from('installment_schedule')
          .select(`
            id, service_order_id, plan_months, installments_paid,
            installments_total, installment_cents, last_failure_at,
            escalation_step,
            service_order:service_order_id (customer_id, total_paid_cents)
          `)
          .not('last_failure_at', 'is', null)
          .lt('escalation_step', 6)
          .order('last_failure_at', { ascending: true });

        if (dbErr) throw dbErr;

        // Supabase JS returns nested selects as either an array or null.
        // Normalise via `unknown` cast.
        type RawRow = {
          id: string;
          service_order_id: string;
          plan_months: number;
          installments_paid: number;
          installments_total: number;
          installment_cents: number;
          last_failure_at: string;
          escalation_step: number;
          service_order: Array<{ customer_id: string; total_paid_cents: number }> | { customer_id: string; total_paid_cents: number } | null;
        };
        const raw = (data ?? []) as unknown as RawRow[];

        function firstSo(r: RawRow): { customer_id: string; total_paid_cents: number } | null {
          if (!r.service_order) return null;
          if (Array.isArray(r.service_order)) return r.service_order[0] ?? null;
          return r.service_order;
        }

        const userIds = raw.map((r) => firstSo(r)?.customer_id).filter(Boolean) as string[];

        const emailMap = new Map<string, string>();
        if (userIds.length > 0) {
          const { data: emailRows } = await supabase
            .from('customer_profile')
            .select('id, billing_email')
            .in('id', userIds);
          (emailRows ?? []).forEach((r) => {
            const row = r as { id: string; billing_email: string | null };
            if (row.billing_email) emailMap.set(row.id, row.billing_email);
          });
        }

        const mapped: RecoveryRow[] = raw.map((r) => {
          const so = firstSo(r);
          return {
            id: r.id,
            service_order_id: r.service_order_id,
            customer_id: so?.customer_id ?? '',
            customer_email: so?.customer_id ? emailMap.get(so.customer_id) ?? null : null,
            plan_months: r.plan_months,
            installments_paid: r.installments_paid,
            installments_total: r.installments_total,
            installment_cents: r.installment_cents,
            last_failure_at: r.last_failure_at,
            escalation_step: r.escalation_step,
            total_paid_cents: so?.total_paid_cents ?? 0,
          };
        });

        if (!cancelled) {
          setRows(mapped);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const totalAtRisk = useMemo(
    () => rows.reduce((s, r) => s + (399000 - r.total_paid_cents), 0),
    [rows]
  );

  /**
   * SEARCH ONLY (§4.2 classified this "none"). These are CURRENT arrears — a
   * stock set defined by "still owing", not by when anything happened. Dating
   * it would answer a different question than the one the page is opened with,
   * which is "who do I chase today".
   */
  const table = useTableFilters<RecoveryRow>({
    rows,
    searchText: (r) => r.customer_email ?? '',
  });

  const columns = useMemo<UniversalTableColumn<RecoveryRow>[]>(
    () => [
      {
        key: 'days',
        header: 'Days since failure',
        sortValue: (r) => daysSince(r.last_failure_at),
        render: (r) => {
          const d = daysSince(r.last_failure_at);
          return (
            <span className="font-medium text-foreground">
              {d} day{d === 1 ? '' : 's'}
            </span>
          );
        },
      },
      {
        key: 'step',
        header: 'Escalation step',
        sortValue: (r) => r.escalation_step,
        render: (r) => {
          const meta = ESCALATION_LABEL[r.escalation_step] ?? ESCALATION_LABEL[0]!;
          return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
        },
      },
      {
        key: 'customer',
        header: 'Customer',
        render: (r) => (
          <span className="text-foreground text-sm">
            {r.customer_email ?? r.customer_id.slice(0, 8)}
          </span>
        ),
      },
      {
        key: 'progress',
        header: 'Progress',
        sortValue: (r) => r.installments_paid / r.installments_total,
        render: (r) => (
          <span className="text-sm text-foreground">
            {r.installments_paid} / {r.installments_total} paid
          </span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'monthly',
        header: 'Monthly',
        sortValue: (r) => r.installment_cents,
        render: (r) => <span className="text-sm">{formatZARFromCents(r.installment_cents)}</span>,
        hideOnMobile: true,
      },
      {
        key: 'outstanding',
        header: 'Outstanding',
        sortValue: (r) => 399000 - r.total_paid_cents,
        render: (r) => (
          <span className="font-medium text-warning-dark">
            {formatZARFromCents(399000 - r.total_paid_cents)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        render: (r) => (
          <Link
            to={`/admin/services/${r.service_order_id}`}
            className="inline-flex items-center gap-1 text-sm text-primary-ink hover:underline"
          >
            Open
            <ExternalLink className="w-3 h-3" />
          </Link>
        ),
      },
    ],
    []
  );

  return (
    <UniversalPage
      title="Installment recovery queue"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Services', href: '/admin/services' },
        { label: 'Installment recovery' },
      ]}
    >
      {error && (
        <NotificationCard
          tone="danger"
          title="Could not load installment recovery queue"
          body={error.message}
        />
      )}

      <Card>
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              At-risk installments
            </p>
            <p className="text-2xl font-bold text-foreground mt-1">{rows.length}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Outstanding total
            </p>
            <p className="text-2xl font-bold text-warning-dark mt-1">
              {formatZARFromCents(totalAtRisk)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Day-30+ (suspended)
            </p>
            <p className="text-2xl font-bold text-foreground mt-1">
              {rows.filter((r) => r.escalation_step >= 4).length}
            </p>
          </div>
        </div>
      </Card>

      <div className="mt-4">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <Card>
            <div className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-success mx-auto mb-3" />
              <p className="text-foreground font-medium">No at-risk installments</p>
              <p className="text-sm text-muted-foreground mt-1">
                The Track B cron walks this list daily — anything appearing here
                is an installment that failed and needs follow-up.
              </p>
            </div>
          </Card>
        ) : (
          <>
            <div className="mb-3 flex justify-end">
              <SearchField
                value={table.q}
                onValueChange={table.setQ}
                pending={table.isSearching}
                placeholder="Search customer email"
                className="w-full max-w-xs"
              />
            </div>
            <UniversalTable columns={columns} data={table.rows} caption={table.caption} />
          </>
        )}
      </div>
    </UniversalPage>
  );
}
